import moment from "moment";
import { UserBooking } from "../../APIService/UserBookingService";
import { BookingCardAction } from "./work_place";
import { useTranslation } from "react-i18next";
import BookingActionButton from "../BookingActionButton/BookingActionButton";
import { useEffect, useState } from "react";

type BookingCardProps = {
    booking: UserBooking;
    theme: string | null;
    onAction: (type: BookingCardAction) => void;
}

const BookingCard = ({ booking, theme, onAction }: BookingCardProps) => {

    const { t } = useTranslation();
    const [now, setNow] = useState(moment());

    useEffect(() => {
    const interval = setInterval(() => {
        setNow(moment());
    }, 1000); // update every minute

    return () => clearInterval(interval);
}, []);

    const getActionShadow = () => {
        const now = moment();
        const noShadow = "0 0 1px 0.5px rgba(255, 255, 255, 0)";

        const start = moment(booking.Start);
        const end = moment(booking.End);
        const startIn = booking.StartIn ? moment(booking.StartIn) : null;
        const endsIn = booking.EndsIn ? moment(booking.EndsIn) : null;

        // Only today
        if (!start.isSame(now, 'day')) {
            return noShadow;
        }

        // 1️⃣ ENDED → no shadow
        if (endsIn) {
            return noShadow;
        }

        // 2️⃣ STARTED → red shadow
        if (startIn) {
            return "0 0 1px 0.5px rgba(255, 0, 80, 0.5)";
        }

        // 3️⃣ NOT STARTED → within 30 minutes only
        if (now.isBefore(start)) {
            const minutesToStart = start.diff(now, 'minutes');

            if (minutesToStart > 30) {
                return noShadow;
            }

            return "0 0 1px 0.5px rgba(255, 170, 0, 0.5)";
        }

        return noShadow;
    };

    const getMeetingStatus = () => {
        // const now = moment();

        const start = moment(booking.Start);
        const end = moment(booking.End);
        const startIn = booking.StartIn ? moment(booking.StartIn) : null;
        const endsIn = booking.EndsIn ? moment(booking.EndsIn) : null;
        const minutesToStart = start.diff(now, "minutes");

        // Only today
        if (!start.isSame(now, "day") || minutesToStart > 60) {
            return null;
        }

        // 1️⃣ ENDED (highest priority)
        if (endsIn) {
            return `${t('EndedAt')} ${endsIn.format("hh:mm a")}`;
        }

        // 2️⃣ STARTED
        if (startIn) {
            const minutes = end.diff(now, "minutes");
            return formatDuration(minutes, t('Ending'));
        }

        // 3️⃣ NOT STARTED
        if (now.isBefore(start)) {
            const minutes = start.diff(now, "minutes");
            return formatDuration(minutes, t('Starting'));
        }

        return null;
    };


    const formatDuration = (minutes: number, prefix: string) => {
        if (minutes <= 0) {
            return `${prefix} now`;
        }

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        if (hours > 0 && remainingMinutes > 0) {
            return `${prefix} in ${hours} hour${hours > 1 ? "s" : ""} ${remainingMinutes} minute${remainingMinutes > 1 ? "s" : ""}`;
        }

        if (hours > 0) {
            return `${prefix} in ${hours} hour${hours > 1 ? "s" : ""}`;
        }

        return `${prefix} in ${minutes} minute${minutes > 1 ? "s" : ""}`;
    };


    const meetingStatus = getMeetingStatus();

    return (
        <>
            <div className="rounded w-100 p-2 d-flex flex-column gap-2" style={{ border: theme === "dark" ? "1px solid #525252ff" : "2px solid #e8e8e8ff", boxShadow: getActionShadow() }}>
                <div className="d-flex justify-content-between align-items-center">
                    <span className="px-2 py-1 rounded-1" style={{ backgroundColor: meetingStatus ? theme === "dark" ? "#ffffff3d" : '#0000003d' : "transparent", color: theme === "dark" ? 'white' : "black" }}>
                        {meetingStatus}
                    </span>
                    <div className='d-flex gap-2'>
                        {
                            booking.Actions.CheckIn &&
                            <BookingActionButton
                                theme={theme ?? "light"}
                                icon={theme === 'dark' ? "/checkin_act/dark.svg" : '/checkin_act/light.svg'}
                                label={t('CheckIn')}
                                bgColor="#FFAE00"
                                onClick={() => onAction(BookingCardAction.CheckIn)}
                            />
                        }
                        {
                            booking.Actions.CheckOut &&
                            <BookingActionButton
                                theme={theme ?? "light"}
                                icon={theme === 'dark' ? '/checkoutlight.svg' : '/checkout.svg'}
                                label={t('CheckOut')}
                                bgColor="#FF5252"
                                onClick={() => onAction(BookingCardAction.CheckOut)}
                            />

                        }
                        {
                            booking.Actions.Cancel &&
                            <BookingActionButton
                                theme={theme ?? "dark"}
                                icon={theme === 'dark' ? '/checkoutlight.svg' : '/checkout.svg'}
                                label={t('Cancel')}
                                bgColor="transparent"
                                isCancel={true}
                                onClick={() => onAction(BookingCardAction.Cancel)}
                            />
                        }
                        {
                            booking.Actions.Extend &&
                            <BookingActionButton
                                theme={theme ?? "light"}
                                icon={theme === 'dark' ? '/extend_btn/light.svg' : '/extend_btn/dark.svg'}
                                label={t('Extend')}
                                bgColor="#0066FF"
                                onClick={() => onAction(BookingCardAction.Extend)}
                            />
                        }
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                    <span className="px-1 py-1 fw-bold" style={{ fontSize: 14 }}>
                        {booking.Subject}
                    </span>
                    <span className="" style={{ fontSize: 14 }}>
                        {moment(booking?.Start).format("HH:mm")}
                        {' '}-{' '}
                        {moment(booking?.End).format("HH:mm")}
                    </span>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                    <span className="px-1" style={{ color: "#abababff" }}>
                        {booking.Resource?.Name}
                    </span>
                    {/* <div className="ps-2" style={{ cursor: 'pointer', color: "#abababff" }}>
                        <CiLocationOn size="20" />
                    </div> */}
                </div>
            </div>
        </>
    )
}

export default BookingCard;